import { useEffect, useState } from "react";
import { UtensilsCrossed, ArrowUpRight, Check } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import Reveal, { Caption } from "@/components/Reveal";

export default function FoodDiningSection({ onRequest }) {
  const { t, lang } = useLang();
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    api.get("/food-services/menu").then((r) => setMenu(r.data)).catch(() => {});
  }, []);

  const items = (menu?.items || []).filter((i) => i.active);
  const conditions = ["c1", "c2", "c3", "c4", "c5", "c6", "c7"];

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-24" data-testid="food-section">
      <Reveal>
        <div className="bg-white rounded-[2rem] border border-terracotta/10 overflow-hidden">
          <div className="p-7 sm:p-10 border-b border-ink/5 bg-gradient-to-br from-sand via-white to-gold/10">
            <Caption>{t("food.caption")}</Caption>
            <div className="flex items-center gap-3 mt-3">
              <span className="w-11 h-11 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center">
                <UtensilsCrossed size={21} />
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl text-ink leading-tight">{t("food.title")}</h2>
            </div>
            <p className="text-ink/60 text-sm sm:text-base leading-relaxed mt-4 max-w-3xl" data-testid="food-intro">
              {t("food.intro")}
            </p>
          </div>

          <div className="p-7 sm:p-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
            {items.map((i) => (
              <div key={i.id} data-testid={`food-menu-${i.id}`}
                   className="flex items-baseline justify-between gap-4 border-b border-ink/5 py-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{lang === "es" ? i.label_es : i.label_en}</p>
                  <p className="text-xs text-ink/50 mt-0.5">{lang === "es" ? i.desc_es : i.desc_en}</p>
                </div>
                {i.price ? (
                  <span className="text-sm font-semibold text-terracotta whitespace-nowrap" data-testid={`food-price-${i.id}`}>
                    ${fmtUSD(i.price)} <span className="text-[11px] font-normal text-ink/45">{t(i.per_day ? "food.perPersonDay" : "food.perPerson")}</span>
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-teal whitespace-nowrap" data-testid={`food-price-${i.id}`}>{t("food.quote")}</span>
                )}
              </div>
            ))}
          </div>

          <div className="px-7 sm:px-10 pb-8">
            <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/45 mb-3">{t("food.conditionsTitle")}</p>
            <ul className="space-y-2">
              {conditions.map((c) => (
                <li key={c} className="flex gap-2.5 text-xs sm:text-sm text-ink/60 leading-relaxed" data-testid={`food-condition-${c}`}>
                  <Check size={14} className="text-teal shrink-0 mt-0.5" /> {t(`food.${c}`)}
                </li>
              ))}
            </ul>
            <button data-testid="food-request-btn" onClick={onRequest}
                    className="mt-8 rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 transition-colors bg-terracotta text-sand hover:bg-terracotta-dark">
              {t("food.requestBtn")} <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
